import {Inject, Injectable, InjectionToken} from '@angular/core';
import {from, Observable} from 'rxjs';
import {map, mergeMap, toArray} from 'rxjs/operators';
import {Equipment, EquipmentId} from '@app/graphql-typings/graphql';
import {SiteId} from '@shared/services/api/access-sites.interface';
import {EquipmentService} from '@shared/services/api/equipment.service';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {CacheByParentId} from './cache-by-parent-id';

/** Configures whether the equipment cache caches or is just a pass through */
export const EQUIPMENT_CACHING_ENABLED = new InjectionToken<boolean>('EquipmentCachingEnabled', {
  providedIn: 'root',
  factory: () => true
});

export abstract class EquipmentCache {
  abstract getEquipmentForSite(siteId: SiteId): Observable<Equipment[]>;
  abstract getEquipmentByIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Equipment>>;
}

@Injectable()
export class EquipmentCacheImpl implements EquipmentCache {
  private readonly cache: CacheByParentId<SiteId, EquipmentId, Equipment>;

  constructor(private readonly equipmentService: EquipmentService,
              @Inject(EQUIPMENT_CACHING_ENABLED) cachingEnabled = true) {
    this.cache = new CacheByParentId<SiteId, EquipmentId, Equipment>(
      (equipmentIds: EquipmentId[]) => this.fetchByIds(equipmentIds),
      (siteIds: SiteId[]) => this.fetchBySiteIds(siteIds),
      cachingEnabled
    );
  }

  getEquipmentForSite(siteId: SiteId): Observable<Equipment[]> {
    return this.cache.getListByParentId(siteId);
  }

  getEquipmentByIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Equipment>> {
    return this.cache.getByIds(equipmentIds);
  }

  private fetchByIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Equipment>> {
    return this.equipmentService.getEquipmentByIds(equipmentIds).pipe(
      map(equipment => CollectionUtils.indexBy(equipment, 'equipment_id')));
  }

  /** One request per site, because without the collector the response cannot say which site each equipment came from */
  private fetchBySiteIds(siteIds: SiteId[]): Observable<Map<SiteId, Map<EquipmentId, Equipment>>> {
    const maxConcurrentRequests = 2;
    return from(siteIds).pipe(
      mergeMap(siteId => this.equipmentService.getEquipmentForSites([siteId], false).pipe(
        map(equipment => <[SiteId, Map<EquipmentId, Equipment>]>[siteId, CollectionUtils.indexBy(equipment, 'equipment_id')])
      ), maxConcurrentRequests),
      toArray(),
      map(equipmentByIdBySiteId => new Map(equipmentByIdBySiteId))
    );
  }
}
